/**
 * StaffManagementPage - Pagină pentru gestionarea angajaților
 * Accesibilă din Configurare > Angajați
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Autocomplete,
  Divider,
  CircularProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocalHospital as DoctorIcon,
  Engineering as TechnicianIcon,
  AccountCircle as AccountIcon,
} from '@mui/icons-material'
import {
  Staff,
  StaffType,
  StaffCreateData,
  StaffUpdateData,
  StaffWithInstitutions,
  StaffListResponse,
  getStaffList,
  getStaff,
  createStaff,
  updateStaff,
  deleteStaff,
  getStaffTypeLabel,
  getAllStaffTypes,
  migrateUsersToStaff,
  StaffMigrationResult,
} from '../api/staffApi'
import apiClient from '../services/api'

interface Institution {
  id: string
  name: string
  code?: string
}

interface Role {
  id: string
  name: string
}

const StaffManagementPage: React.FC = () => {
  // State
  const [staff, setStaff] = useState<Staff[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  
  // Filters
  const [search, setSearch] = useState('')
  const [staffTypeFilter, setStaffTypeFilter] = useState<StaffType | ''>('')
  const [institutionFilter, setInstitutionFilter] = useState<string>('')
  const [activeFilter, setActiveFilter] = useState<boolean | null>(true)
  
  // Reference data
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false)
  const [editingStaff, setEditingStaff] = useState<StaffWithInstitutions | null>(null)
  const [formData, setFormData] = useState<StaffCreateData>({
    first_name: '',
    last_name: '',
    staff_type: 'technician',
    phone: '',
    email: '',
    cnp: '',
    cod_parafa: '',
    modalities: [],
    is_coordinator: false,
    teleradiology_only: false,
    active: true,
    notes: '',
    institution_ids: [],
    create_user_account: false,
    user_email: '',
    user_password: '',
  })
  
  // Migration dialog
  const [openMigrationDialog, setOpenMigrationDialog] = useState(false)
  const [migrationResult, setMigrationResult] = useState<StaffMigrationResult | null>(null)
  const [migrationLoading, setMigrationLoading] = useState(false)
  
  // Notifications
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Load data
  useEffect(() => {
    loadStaff()
  }, [page, pageSize, search, staffTypeFilter, institutionFilter, activeFilter])

  useEffect(() => {
    loadInstitutions()
    loadRoles()
  }, [])

  const loadStaff = async () => {
    setLoading(true)
    try {
      const response = await getStaffList({
        search: search || undefined,
        staff_type: staffTypeFilter || undefined,
        institution_id: institutionFilter || undefined,
        active: activeFilter ?? undefined,
        page: page + 1,
        page_size: pageSize,
      })
      setStaff(response.items)
      setTotal(response.total)
    } catch (error) {
      console.error('Error loading staff:', error)
      setSnackbar({ open: true, message: 'Eroare la încărcarea angajaților', severity: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadInstitutions = async () => {
    try {
      const response = await apiClient.get('/tenants/institutions')
      setInstitutions(response.data || [])
    } catch (error) {
      console.error('Error loading institutions:', error)
    }
  }

  const loadRoles = async () => {
    try {
      const response = await apiClient.get('/permissions/roles')
      setRoles(response.data?.roles || [])
    } catch (error) {
      console.error('Error loading roles:', error)
    }
  }

  const handleOpenDialog = async (staffItem?: Staff) => {
    if (staffItem) {
      try {
        const fullStaff = await getStaff(staffItem.id)
        setEditingStaff(fullStaff)
        setFormData({
          first_name: fullStaff.first_name,
          last_name: fullStaff.last_name,
          staff_type: fullStaff.staff_type as StaffType,
          phone: fullStaff.phone || '',
          email: fullStaff.email || '',
          cnp: fullStaff.cnp || '',
          cod_parafa: fullStaff.cod_parafa || '',
          modalities: fullStaff.modalities || [],
          is_coordinator: fullStaff.is_coordinator,
          teleradiology_only: fullStaff.teleradiology_only,
          active: fullStaff.active,
          notes: fullStaff.notes || '',
          institution_ids: fullStaff.institution_assignments.map(a => a.institution_id),
          create_user_account: false,
        })
      } catch (error) {
        console.error('Error loading staff details:', error)
        setSnackbar({ open: true, message: 'Eroare la încărcarea detaliilor', severity: 'error' })
        return
      }
    } else {
      setEditingStaff(null)
      setFormData({
        first_name: '',
        last_name: '',
        staff_type: 'technician',
        phone: '',
        email: '',
        cnp: '',
        cod_parafa: '',
        modalities: [],
        is_coordinator: false,
        teleradiology_only: false,
        active: true,
        notes: '',
        institution_ids: [],
        create_user_account: false,
        user_email: '',
        user_password: '',
      })
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingStaff(null)
  }

  const handleSubmit = async () => {
    try {
      if (!formData.first_name || !formData.last_name) {
        setSnackbar({ open: true, message: 'Numele și prenumele sunt obligatorii', severity: 'error' })
        return
      }

      // Convert empty strings to undefined for optional fields
      const cleanData = {
        ...formData,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        cnp: formData.cnp || undefined,
        cod_parafa: formData.cod_parafa || undefined,
        notes: formData.notes || undefined,
        user_email: formData.user_email || undefined,
        user_password: formData.user_password || undefined,
        institution_ids: formData.institution_ids?.length ? formData.institution_ids : undefined,
        modalities: formData.modalities?.length ? formData.modalities : undefined,
      }

      if (editingStaff) {
        const updateData: StaffUpdateData = {
          first_name: cleanData.first_name,
          last_name: cleanData.last_name,
          staff_type: cleanData.staff_type,
          phone: cleanData.phone,
          email: cleanData.email,
          cnp: cleanData.cnp,
          cod_parafa: cleanData.cod_parafa,
          modalities: cleanData.modalities,
          is_coordinator: cleanData.is_coordinator,
          teleradiology_only: cleanData.teleradiology_only,
          active: cleanData.active,
          notes: cleanData.notes,
        }
        await updateStaff(editingStaff.id, updateData)
        setSnackbar({ open: true, message: 'Angajat actualizat cu succes', severity: 'success' })
      } else {
        await createStaff(cleanData)
        setSnackbar({ open: true, message: 'Angajat creat cu succes', severity: 'success' })
      }

      handleCloseDialog()
      loadStaff()
    } catch (error: any) {
      console.error('Error saving staff:', error)
      let message = 'Eroare la salvarea angajatului'
      const detail = error.response?.data?.detail
      if (typeof detail === 'string') {
        message = detail
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors - array of { type, loc, msg, ... }
        message = detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ')
      }
      setSnackbar({ open: true, message, severity: 'error' })
    }
  }

  const handleDelete = async (staffItem: Staff) => {
    if (!window.confirm(`Sigur doriți să ștergeți angajatul ${staffItem.full_name || `${staffItem.first_name} ${staffItem.last_name}`}?`)) {
      return
    }

    try {
      await deleteStaff(staffItem.id)
      setSnackbar({ open: true, message: 'Angajat șters cu succes', severity: 'success' })
      loadStaff()
    } catch (error) {
      console.error('Error deleting staff:', error)
      setSnackbar({ open: true, message: 'Eroare la ștergerea angajatului', severity: 'error' })
    }
  }

  const handleMigration = async (dryRun: boolean) => {
    setMigrationLoading(true)
    try {
      const result = await migrateUsersToStaff(dryRun)
      setMigrationResult(result)
      if (!dryRun) {
        loadStaff()
        setSnackbar({ open: true, message: `Migrare completă: ${result.staff_created} angajați creați`, severity: 'success' })
      }
    } catch (error) {
      console.error('Error during migration:', error)
      setSnackbar({ open: true, message: 'Eroare la migrare', severity: 'error' })
    } finally {
      setMigrationLoading(false)
    }
  }

  const getStaffTypeIcon = (type: StaffType) => {
    switch (type) {
      case 'doctor':
        return <DoctorIcon fontSize="small" />
      case 'technician':
        return <TechnicianIcon fontSize="small" />
      default:
        return <PersonIcon fontSize="small" />
    }
  }

  const availableModalities = ['CR', 'CT', 'DR', 'DS', 'DX', 'ECG', 'ES', 'IO', 'MG', 'MR', 'NM', 'OCT', 'OT', 'PT', 'PX', 'RF', 'SR', 'US', 'XA', 'XC', 'XR']

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Angajați
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setOpenMigrationDialog(true)}
            startIcon={<AccountIcon />}
          >
            Migrare din Utilizatori
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Adaugă Angajat
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Caută după nume, email, telefon..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tip</InputLabel>
                <Select
                  value={staffTypeFilter}
                  onChange={(e) => setStaffTypeFilter(e.target.value as StaffType | '')}
                  label="Tip"
                >
                  <MenuItem value="">Toate</MenuItem>
                  {getAllStaffTypes().map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Instituție</InputLabel>
                <Select
                  value={institutionFilter}
                  onChange={(e) => setInstitutionFilter(e.target.value)}
                  label="Instituție"
                >
                  <MenuItem value="">Toate</MenuItem>
                  {institutions.map((inst) => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={activeFilter === null ? '' : activeFilter ? 'active' : 'inactive'}
                  onChange={(e) => {
                    const value = e.target.value
                    setActiveFilter(value === '' ? null : value === 'active')
                  }}
                  label="Status"
                >
                  <MenuItem value="">Toți</MenuItem>
                  <MenuItem value="active">Activi</MenuItem>
                  <MenuItem value="inactive">Inactivi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nume</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Instituții</TableCell>
                <TableCell>Cont</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : staff.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nu s-au găsit angajați</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                staff.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStaffTypeIcon(item.staff_type as StaffType)}
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {item.display_name || `${item.first_name} ${item.last_name}`}
                          </Typography>
                          {item.cnp && (
                            <Typography variant="caption" color="text.secondary">
                              CNP: {item.cnp}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStaffTypeLabel(item.staff_type as StaffType)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.email || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.phone || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* TODO: Show institution count or first institution */}
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    </TableCell>
                    <TableCell>
                      {item.has_user_account ? (
                        <Tooltip title={item.user_email || 'Are cont'}>
                          <Chip
                            icon={<AccountIcon />}
                            label="Da"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        <Chip label="Nu" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.active ? 'Activ' : 'Inactiv'}
                        size="small"
                        color={item.active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editează">
                        <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Șterge">
                        <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Per pagină:"
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStaff ? 'Editează Angajat' : 'Adaugă Angajat Nou'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Informații Personale
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Prenume"
                required
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Nume"
                required
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tip Angajat</InputLabel>
                <Select
                  value={formData.staff_type}
                  onChange={(e) => setFormData({ ...formData, staff_type: e.target.value as StaffType })}
                  label="Tip Angajat"
                >
                  {getAllStaffTypes().map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="CNP"
                value={formData.cnp}
                onChange={(e) => setFormData({ ...formData, cnp: e.target.value })}
                inputProps={{ maxLength: 13 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Telefon"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>

            {/* Doctor-specific fields */}
            {formData.staff_type === 'doctor' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Informații Medic
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Cod Parafă"
                    value={formData.cod_parafa}
                    onChange={(e) => setFormData({ ...formData, cod_parafa: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={availableModalities}
                    value={formData.modalities || []}
                    onChange={(_, newValue) => setFormData({ ...formData, modalities: newValue })}
                    renderInput={(params) => (
                      <TextField {...params} label="Modalități" placeholder="Selectează..." />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                      ))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_coordinator}
                        onChange={(e) => setFormData({ ...formData, is_coordinator: e.target.checked })}
                      />
                    }
                    label="Medic Coordonator"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.teleradiology_only}
                        onChange={(e) => setFormData({ ...formData, teleradiology_only: e.target.checked })}
                      />
                    }
                    label="Doar Teleradiologie"
                  />
                </Grid>
              </>
            )}

            {/* Institutions */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Instituții
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                size="small"
                options={institutions}
                getOptionLabel={(option) => option.name}
                value={institutions.filter((i) => formData.institution_ids?.includes(i.id))}
                onChange={(_, newValue) =>
                  setFormData({ ...formData, institution_ids: newValue.map((i) => i.id) })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Instituții" placeholder="Selectează..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                      icon={<BusinessIcon />}
                    />
                  ))
                }
              />
            </Grid>

            {/* User Account (only for new staff) */}
            {!editingStaff && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Cont de Utilizator
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.create_user_account}
                        onChange={(e) => setFormData({ ...formData, create_user_account: e.target.checked })}
                      />
                    }
                    label="Creează cont de acces pentru acest angajat"
                  />
                </Grid>
                {formData.create_user_account && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Email cont"
                        type="email"
                        required
                        value={formData.user_email}
                        onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Parolă"
                        type="password"
                        required
                        value={formData.user_password}
                        onChange={(e) => setFormData({ ...formData, user_password: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Rol</InputLabel>
                        <Select
                          value={formData.user_role_id || ''}
                          onChange={(e) => setFormData({ ...formData, user_role_id: e.target.value })}
                          label="Rol"
                        >
                          {roles.map((role) => (
                            <MenuItem key={role.id} value={role.id}>
                              {role.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </>
            )}

            {/* Status and Notes */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                }
                label="Activ"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Note"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Anulează</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingStaff ? 'Salvează' : 'Adaugă'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Migration Dialog */}
      <Dialog open={openMigrationDialog} onClose={() => setOpenMigrationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Migrare Utilizatori în Angajați</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Acest proces va migra utilizatorii existenți (medici, tehnicieni, operatori) în tabelul de angajați.
          </Typography>
          <Typography paragraph color="text.secondary">
            Utilizatorii vor fi păstrați și legați de înregistrările de angajați create.
            Nu se vor pierde date și nu se vor duplica înregistrări.
          </Typography>

          {migrationResult && (
            <Alert severity={migrationResult.errors.length > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Utilizatori procesați:</strong> {migrationResult.total_users_processed}<br />
                <strong>Angajați creați:</strong> {migrationResult.staff_created}<br />
                <strong>Omise (deja existau):</strong> {migrationResult.staff_skipped}<br />
                <strong>Asocieri instituții:</strong> {migrationResult.institution_assignments_created}
              </Typography>
              {migrationResult.errors.length > 0 && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Erori: {migrationResult.errors.join(', ')}
                </Typography>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMigrationDialog(false)}>Închide</Button>
          <Button
            onClick={() => handleMigration(true)}
            disabled={migrationLoading}
            variant="outlined"
          >
            {migrationLoading ? <CircularProgress size={20} /> : 'Simulare (Dry Run)'}
          </Button>
          <Button
            onClick={() => handleMigration(false)}
            disabled={migrationLoading}
            variant="contained"
            color="warning"
          >
            {migrationLoading ? <CircularProgress size={20} /> : 'Execută Migrarea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default StaffManagementPage
